// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatDetailsPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CampaignDocsPanel.test.tsx
import { CircleNotch, File } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useState } from "react";

import { downloadAsset } from "../../../utils/http";

/**
 * Gray chip used for non-media file attachments in Ask Puntt chat messages,
 * the Details rail Uploaded files list, and project Reference Documents.
 */
export function FileAttachmentButton({
  url,
  name,
  testId = "chat-file-attachment",
  versionOrdinal,
  ariaLabel,
}: {
  url: string;
  name?: string;
  /** Override for per-file ids in Details / project docs rails. */
  testId?: string;
  /** Optional version badge (project Reference Documents). */
  versionOrdinal?: number;
  /** Override accessible name (e.g. include version). */
  ariaLabel?: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const canDownload = Boolean(url.trim());

  const handleClick = async () => {
    if (downloading || !canDownload) return;
    setDownloading(true);
    try {
      await downloadAsset(url, name ?? undefined);
    } catch (err) {
      console.error("Failed to download attachment", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading || !canDownload}
      data-testid={testId}
      // text-left overrides the <button> default of text-align: center so
      // wrapped filenames stay left-aligned next to the file icon instead
      // of drifting into the middle of the chip.
      className="flex w-full min-w-0 max-w-full items-start gap-2 overflow-hidden rounded-xl bg-puntt-neutral-gray-5 px-2 py-1.5 text-left text-puntt-neutral-gray-12 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
      aria-label={ariaLabel ?? `Download attachment ${name ?? ""}`.trim()}
    >
      {downloading ? (
        <CircleNotch size={16} className="mt-0.5 shrink-0 animate-spin" />
      ) : (
        <File size={16} className="mt-0.5 shrink-0" />
      )}
      <Text size="2" className="line-clamp-2 min-w-0 flex-1 break-all">
        {name ?? "Download"}
      </Text>
      {versionOrdinal != null ? (
        <span
          className="font-mono mt-0.5 shrink-0 rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
          data-testid={`${testId}-version`}
        >
          v{versionOrdinal}
        </span>
      ) : null}
    </button>
  );
}
